import * as moment from 'moment-timezone';
import * as api from '@crochik/schedulerapi.ts';

import { Default, IForm } from '@crochik/pi-react/context';
import { IApp } from '@crochik/pi-react/context/IApp';
import { App } from '@crochik/pi-react/application/App';
import { Form } from '@crochik/pi-react/ui/Form';
import Dialog from '@crochik/pi-react/services/Dialog';

import defaultActions from './actions';
import defaultServices from './services';
import defaultPages from './pages';

import { createSession, init } from './services/API';

export interface CurrentURL {
    path: string;
    search?: string;
    searchParams?: { [name: string]: string };
    referrer?: string;
}

const contactForm: IForm = {
    name: 'Contact',
    title: 'Enter your Contact Information',
    fields: [
        [
            {
                name: 'firstName',
                label: 'First Name',
                type: 'text',
                isRequired: true,
            },
            {
                name: 'lastName',
                label: 'Last Name',
                type: 'text',
                isRequired: true,
            },
        ],
        {
            name: 'phone',
            label: 'Phone Number',
            type: 'phone',
            isRequired: true,
        },
        {
            name: 'email',
            label: 'E-mail',
            type: 'email',
            isRequired: true,
        },
        {
            label: 'Address',
            name: 'address',
            type: 'address'
        },
        [
            {
                label: 'Postal Code',
                name: 'postalCode',
                type: 'postalcode',
                visible: ['address'],
            },
            {
                label: 'City',
                name: 'city',
                type: 'text',
                visible: ['address'],
            }
        ],
        [
            {
                label: 'State',
                name: 'state',
                type: 'text',
                visible: ['address'],
            },
            {
                label: 'Country',
                name: 'country',
                type: 'text',
                visible: ['address'],
            }
        ]
    ],
    actions: [
        {
            name: 'next',
            action: '.app.createLead', // '.app.schedule',
            label: 'Next',
            enable: [
                'firstName',
                'lastName',
                'phone',
                'email'
            ]
        }
    ],
};

const appointmentForm: IForm = {
    name: 'Appointment',
    fields: [
        {
            name: 'appointmentTypeId',
            type: 'hidden'
        },
        {
            name: 'start',
            type: 'hidden'
        },
        {
            name: 'end',
            type: 'hidden'
        }
    ]
};

const leadForm: IForm = {
    name: 'Lead',
    title: 'Existing Lead',
    fields: [
        {
            name: 'name',
            label: 'Name',
            type: 'text',
            isRequired: true,
        },
        {
            name: 'phone',
            label: 'Phone Number',
            type: 'phone',
            isRequired: true,
        },
        {
            name: 'email',
            label: 'E-mail',
            type: 'email',
            isRequired: true,
        },
    ]
};

const initialApp: IApp = {
    name: 'ᴨ Booking',
    disableHistory: true,
    ga: process.env.REACT_APP_GA,
    // initialPage: 'undefined',
    // menu: 'app'
};

interface StartArgs {
    apptType?: string;
    organization?: string;
    auth?: string;
}

function criticalError(title: string, message: string) {
    document.getElementsByClassName('SimpleChrome')[0]['style']['display'] = 'none';

    Dialog.critical({
        title,
        message
    });

    // TODO: on dismiss navigate somewhere else?
}

async function delayInit(app: App) {
    const args = processOriginalRequest();
    if (!args) {
        criticalError('Invalid Link', 'Missing Appointment Type.');
        return;
    }

    // // bind any postalcode fields
    // var forms = Default.ui.get('form');
    // Object.keys(forms).forEach((name) => {
    //     var form = Form.get(name);
    //     Object.keys(form.fieldMap).forEach((fieldName) => {
    //         const field = form.fieldMap[fieldName] as IField;
    //         if (field.type === TYPE.POSTALCODE) {
    //             form.bindOnChange(fieldName, onPostalCodeChange);
    //         }
    //     });
    // });

    await initSession(app, args);

    // if (startArgs.auth) {
    //     var appts = await apiClient.getFutureAppointments();
    //     if (appts.length > 0) {
    //         console.log(`${appts.length} appointment(s) upcoming.`);
    //         // Default.state.assign('Appointment', appts[0], 'form');
    //         app.selectPage('ExistingAppointment', 'Launch', appts);
    //         return;
    //     }
    // }
}

function nullIfEmpty(value: string) {
    return value && value.length > 0 ? value : undefined;
}

function getSession(): CurrentURL {
    var request = window.sessionStorage.getItem('request');
    if (request) {
        console.log('found session');
        console.log(request);
        return JSON.parse(request) as CurrentURL;
    }

    console.log('no session');

    // copy of the code in nav
    var search = location.search;
    var result = {};
    if (search && search.startsWith('?')) {
        var params = search.substring(1).split('&');
        // tslint:disable-next-line:forin
        for (var param in params) {
            var tokens = params[param].split('=');
            if (tokens.length === 2) {
                result[tokens[0]] = decodeURIComponent(tokens[1]);
            }
        }
    }

    var req = {
        search: nullIfEmpty(location.search),
        path: location.pathname,
        searchParams: result,
        referrer: nullIfEmpty(document.referrer)
    };

    sessionStorage.setItem('request', JSON.stringify(req));

    return req;
}

function processOriginalRequest(): StartArgs | undefined {
    var req = getSession();
    Default.state.set('LaunchUrl', req);

    if (!req.searchParams) return undefined;
    const startArgs = (req.searchParams as any) as StartArgs;
    Default.state.set('startArgs', startArgs, 'app');

    return startArgs;
}

async function initSession(app: App, startArgs: StartArgs): Promise<boolean> {
    try {
        let session: api.SessionConfig;

        if (startArgs.auth) {
            // auth, lead
            const apiClient = createSession(startArgs.auth);
            session = await apiClient.initSessionForLead();

            const lead = await apiClient.schedulerApi.getLead(apiClient.sessionId);
            Form.get('Lead').assignValues(lead);
        
            apiClient.slots.then(x => {
                console.log('loaded slots');
            });

            app.selectPage('Calendar');

        } else if (startArgs.organization) {
            // launch code
            const apiClient = await init();
            session = await apiClient.initSessionForOrganization(startArgs.organization);

            apiClient.slots.then(x => {
                console.log('loaded slots');
            });

            app.selectPage('Contact');

        } else {
            throw 'Missing required parameters';
        }

        const localTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log(`Current Time Zone: ${localTimeZoneId}`);
        console.log(`Using Time Zone: ${session.timeZoneId}`);
        moment.tz.setDefault(session.timeZoneId);

        if (localTimeZoneId !== session.timeZoneId) {
            Dialog.critical({
                title: 'Different Time Zone',
                message: `Please note that all dates and times displayed are for a different time zone (${session.timeZoneId}) than your current one (${localTimeZoneId}).`
            });
        }

        // var state: AppointmentFormState = Default.state.get('Appointment', 'form');
        // Object.assign(state, {
        //     appointmentTypeId: startArgs.apptType,
        //     timeZoneId: session.timeZoneId
        // });

        // Default.state.set('appointmentTypeId', apptTypeId, 'form.Appointment');
        return true;

    } catch (e) {
        if (e instanceof Response) {
            var response = e as Response;
            switch (response.status) {
                case 404:
                    Dialog.critical({
                        title: 'Invalid Link',
                        message: 'Invalid Appointment Type.',
                    });
                    return false;

                default:
                    Dialog.critical({
                        title: 'Critical Error',
                        message: response.statusText,
                    });
                    return false;
            }

        }

        var message: string;
        if (e instanceof Error) {
            var error = e as Error;
            message = `${error.name}: "${error.message}"`;

        } else if (typeof (e) === 'string') {
            message = e;

        } else {
            message = 'Undefined error';
        }

        Dialog.critical({ message });
    }

    return false;
}

export default function initApp(): App {
    console.log(`Environment: ${process.env.NODE_ENV}`);

    if (process.env.NODE_ENV === 'development') {
        console.log(process.env);
        window['__pi__'] = Default;
    }

    defaultServices();
    defaultActions();
    defaultPages();

    Form.create(contactForm);
    Form.create(appointmentForm);
    Form.create(leadForm);

    return new App(initialApp, delayInit);
}



// WEBPACK FOOTER //
// ./src/app/index.tsx